import type {Column} from "@tanstack/react-table";
import * as React from "react";
import type {DateRange} from "react-day-picker";
import moment from "moment/min/moment-with-locales";
import {Button} from "@/components/ui/button";
import {Calendar} from "@/components/ui/calendar";
import {Popover, PopoverContent, PopoverTrigger,} from "@/components/ui/popover";
import {Separator} from "@/components/ui/separator";
import {formatDate} from "@/lib/format";
import {CalendarMonth, CancelOutlined} from "@mui/icons-material";

moment.locale("fr");

type DateSelection = Date[] | DateRange;

function getIsDateRange(value: DateSelection): value is DateRange {
    return value && typeof value === "object" && !Array.isArray(value);
}

function parseAsDate(timestamp: number | string | undefined): Date | undefined {
    if (!timestamp) return undefined;
    const numericTimestamp =
        typeof timestamp === "string" ? Number(timestamp) : timestamp;
    const date = new Date(numericTimestamp);
    return !Number.isNaN(date.getTime()) ? date : undefined;
}

function parseColumnFilterValue(value: unknown) {
    if (value === null || value === undefined) {
        return [];
    }

    if (Array.isArray(value)) {
        return value.map((item) => {
            if (typeof item === "number" || typeof item === "string") {
                return item;
            }
            return undefined;
        });
    }

    if (typeof value === "string" || typeof value === "number") {
        return [value];
    }

    return [];
}

interface DataTableDateFilterProps<TData> {
    column: Column<TData, unknown>;
    title?: string;
    multiple?: boolean;
    onDateSelected?: (from?: Date, to?: Date) => void;
    selectedDefaultDate?: Date | { from: Date, to: Date };
}

export function DataTableDateFilter<TData>({
                                               column,
                                               title,
                                               multiple, onDateSelected, selectedDefaultDate
                                           }: DataTableDateFilterProps<TData>) {
    const columnFilterValue = column.getFilterValue();
    const selectedDates = React.useMemo<DateSelection>(() => {
        if (!columnFilterValue) {
            if (multiple && selectedDefaultDate && "from" in selectedDefaultDate) {
                return selectedDefaultDate;
            }
            return [];
        }

        if (multiple) {
            const timestamps = parseColumnFilterValue(columnFilterValue);
            return {
                from: parseAsDate(timestamps[0]),
                to: parseAsDate(timestamps[1]),
            };
        }

        const timestamps = parseColumnFilterValue(columnFilterValue);
        const date = parseAsDate(timestamps[0]);
        return date ? [date] : [];
    }, [columnFilterValue, multiple]);

    const onSelect = React.useCallback(
        (date: Date | DateRange | undefined) => {
            if (!date) {
                column.setFilterValue(undefined);
                return;
            }

            if (multiple && !("getTime" in date)) {
                const from = date.from?.getTime();
                const to = date.to?.getTime();
                column.setFilterValue(from !== undefined || to !== undefined ? [from, to] : undefined);
                if (onDateSelected) {
                    onDateSelected(date.from, date.to);
                }
            } else if (!multiple && "getTime" in date) {
                column.setFilterValue(date.getTime());
                if (onDateSelected) onDateSelected();
            }
        },
        [column, multiple, onDateSelected],
    );

    const onReset = React.useCallback(
        (event: React.MouseEvent) => {
            event.stopPropagation();
            column.setFilterValue(undefined);
            if (onDateSelected && selectedDefaultDate && "from" in selectedDefaultDate) {
                onDateSelected(selectedDefaultDate.from, selectedDefaultDate.to);
            }
        },
        [column],
    );

    const hasValue = React.useMemo(() => {
        if (multiple) {
            if (!getIsDateRange(selectedDates)) return false;
            return selectedDates.from || selectedDates.to;
        }
        if (!Array.isArray(selectedDates)) return false;
        return selectedDates.length > 0;
    }, [multiple, selectedDates]);

    const formatDateRange = React.useCallback((range: DateRange) => {
        if (!range.from && !range.to) return "";
        if (range.from && range.to) {
            return `${moment(range.from).format(column.columnDef.meta?.variant === "monthYearRange" ? "MMM YYYY" : "DD MMM YYYY")} - ${moment(range.to).format(column.columnDef.meta?.variant === "monthYearRange" ? "MMM YYYY" : "DD MMM YYYY")}`;
        }
        return moment(range.from ?? range.to).format(column.columnDef.meta?.variant === "monthYearRange" ? "MMM YYYY" : "DD MMM YYYY");
    }, []);

    const label = React.useMemo(() => {
        if (multiple) {
            if (!getIsDateRange(selectedDates)) return null;
            const hasSelectedDates = selectedDates.from || selectedDates.to;
            const dateText = hasSelectedDates
                ? formatDateRange(selectedDates)
                : "Sélectionner une période";

            return (
                <span className="flex items-center gap-2">
                    <span>{title}</span>
                    {hasSelectedDates && (
                        <>
                            <Separator
                                orientation="vertical"
                                className="mx-0.5 data-[orientation=vertical]:h-4"
                            />
                            <span>{dateText}</span>
                        </>
                    )}
                </span>
            );
        }

        if (getIsDateRange(selectedDates)) return null;

        const hasSelectedDate = selectedDates.length > 0;
        const dateText = hasSelectedDate
            ? formatDate(selectedDates[0])
            : "Selectionner une date";

        return (
            <span className="flex items-center gap-2">
                <span>{title}</span>
                {hasSelectedDate && (
                    <>
                        <Separator
                            orientation="vertical"
                            className="mx-0.5 data-[orientation=vertical]:h-4"
                        />
                        <span>{dateText}</span>
                    </>
                )}
            </span>
        );
    }, [selectedDates, multiple, formatDateRange, title]);

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button variant="outline" size="sm">
                    {hasValue ? (
                        <div
                            role="button"
                            aria-label={`Retirer le filtre "${title}"`}
                            tabIndex={0}
                            onClick={onReset}
                            className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        >
                            <CancelOutlined sx={{width: 18, height: 18}}/>
                        </div>
                    ) : (
                        <CalendarMonth sx={{width: 18, height: 18}}/>
                    )}
                    {label}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
                {multiple ? (
                    <Calendar
                        initialFocus
                        mode="range"
                        monthYearOnly={column.columnDef.meta?.variant === "monthYearRange"}
                        monthYearViewOpenedByDefault={column.columnDef.meta?.variant === "monthYearRange"}
                        defaultMonth={getIsDateRange(selectedDates) && selectedDates.from ? selectedDates.from : new Date()}
                        selected={getIsDateRange(selectedDates) ? selectedDates : undefined}
                        onSelect={onSelect}
                        numberOfMonths={2}
                    />
                ) : (
                    <Calendar
                        initialFocus
                        mode="single"
                        selected={
                            !getIsDateRange(selectedDates) ? selectedDates[0] : undefined
                        }
                        onSelect={onSelect}
                    />
                )}
            </PopoverContent>
        </Popover>
    );
}